import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BACKEND_URL } from "@/lib/api";
import {
  House, MapPin, Ruler, ArrowRight, ShieldCheck, EyeSlash, Users,
  Sparkle, CheckCircle, ArrowLeft,
} from "@phosphor-icons/react";
import ProntoLogo from "@/components/ProntoLogo";
import PhotoUploader from "@/components/PhotoUploader";

/**
 * VendiCasa — Landing pubblica per i proprietari (Mossa 2 · Asta Inversa).
 *
 * Il proprietario carica la sua casa in modo anonimo. Gli agenti della zona
 * vedono solo zona macro + caratteristiche + foto. I contatti del proprietario
 * sono sbloccati solo agli agenti che lui sceglie (1-3).
 *
 * Nessun login richiesto. Al termine, il proprietario riceve un URL privato con
 * token per rivedere le proposte in arrivo.
 *
 * Referral: se URL contiene ?ref=mario-rossi, l'agente viene mostrato come
 * "ti ha invitato" e la sua user_id è memorizzata sul listing per priorità.
 */
export default function VendiCasa() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refSlug = (searchParams.get("ref") || "").trim().toLowerCase() || null;
  const [refAgent, setRefAgent] = useState(null); // { name, agency_name, picture, ... } | null
  const [step, setStep] = useState("intro"); // intro | form | done
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    city: "",
    area: "",
    surface_sqm: "",
    rooms: "",
    bathrooms: "",
    state: "buono",
    target_price: "",
    motivation: "",
    timing: "3-6-mesi",
    notes: "",
    photos: [],
    owner_name: "",
    owner_email: "",
    owner_phone: "",
    consent_marketing: false,
  });

  // Resolve referral slug → agent profile (best-effort, silent fail)
  useEffect(() => {
    if (!refSlug) return;
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/marketplace/ref/${encodeURIComponent(refSlug)}`);
        if (!cancel) setRefAgent(data);
      } catch {
        // slug non valido: silent — l'utente vede la pagina normale.
      }
    })();
    return () => { cancel = true; };
  }, [refSlug]);

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.city || !form.surface_sqm || !form.rooms || !form.owner_name || !form.owner_email || !form.owner_phone) {
      toast.error("Mancano dei campi obbligatori.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        surface_sqm: parseInt(form.surface_sqm, 10),
        rooms: parseInt(form.rooms, 10),
        bathrooms: form.bathrooms ? parseInt(form.bathrooms, 10) : null,
        target_price: form.target_price ? parseInt(form.target_price, 10) : null,
        // Pass the referral slug only if we successfully resolved it
        referred_agent_slug: refAgent ? refSlug : null,
      };
      const { data } = await axios.post(`${BACKEND_URL}/api/marketplace/listings`, payload);
      setResult(data);
      setStep("done");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nell'invio.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F5] text-[#1C1C1A]" data-testid="vendi-casa-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#FAF9F5]/90 backdrop-blur border-b border-[#EAE7DF]">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 h-14 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-[#3F3D38] hover:text-[#1C1C1A]" data-testid="vendi-back-home">
            <ProntoLogo size={28} />
            <span className="font-medium">Pronto</span>
          </button>
          <a href="#come-funziona" className="text-[12.5px] text-[#5A6A4D] font-semibold uppercase tracking-wider">Come funziona</a>
        </div>
      </header>

      {step === "intro" && <Intro onStart={() => setStep("form")} refAgent={refAgent} />}
      {step === "form" && <Form form={form} setF={setF} onBack={() => setStep("intro")} onSubmit={submit} submitting={submitting} refAgent={refAgent} />}
      {step === "done" && <Done result={result} refAgent={refAgent} />}
    </div>
  );
}

function Intro({ onStart, refAgent }) {
  return (
    <main className="max-w-3xl mx-auto px-5 lg:px-8 pt-10 sm:pt-16 pb-20">
      {refAgent && <RefAgentBanner agent={refAgent} variant="intro" />}
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F1F4ED] border border-[#7C8B6E]/25 mb-5">
        <span className="w-1.5 h-1.5 rounded-full bg-[#7C8B6E] animate-pulse" />
        <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#5A6A4D]">Trova il tuo agente · 100% gratuita</span>
      </div>
      <h1 className="font-editorial text-[34px] sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-4" data-testid="vendi-title">
        Vuoi vendere casa?<br/>
        <span className="italic text-[#7C8B6E]">Sono gli agenti a proporsi a te.</span>
      </h1>
      <p className="text-[15.5px] sm:text-lg text-[#3F3D38] leading-relaxed max-w-2xl mb-5">
        Carichi le caratteristiche della tua casa in 2 minuti. Gli agenti della tua zona
        ti inviano proposte di mandato: <strong>commissione, prezzo di listino stimato,
        piano di marketing</strong>. Scegli tu chi contattare. <strong>Resti anonimo</strong> finché
        non decidi.
      </p>
      <div className="bg-[#F1F4ED] border border-[#7C8B6E]/30 rounded-xl p-4 mb-8 max-w-2xl" data-testid="vendi-positioning-note">
        <p className="text-[13.5px] text-[#3F3D38] leading-relaxed">
          <strong className="text-[#5A6A4D]">Pronto non vende case.</strong> Pronto ti fa trovare l'agente immobiliare
          giusto per la tua. Tutte le proposte arrivano da <strong>agenti abilitati con partita IVA</strong>,
          iscritti al ruolo (FIMAA / FIAIP / Confcommercio). Niente fai-da-te, niente privati: chi venderà la tua casa
          sarà sempre un <strong>professionista regolare</strong>.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-10" id="come-funziona">
        <Step n={1} icon={House} title="Carichi la tua casa">
          Zona, mq, locali, foto e motivazione. <strong>Niente nome o indirizzo visibili</strong> agli agenti.
        </Step>
        <Step n={2} icon={Users} title="Gli agenti propongono">
          Ricevi proposte di mandato in 24-72h. Commissione, prezzo, marketing.
        </Step>
        <Step n={3} icon={CheckCircle} title="Firmi il mandato con 1-3 agenti">
          Solo loro vedono i tuoi contatti. Da quel momento sono <strong>loro</strong> a vendere la casa: trattativa, visite, rogito.
        </Step>
      </div>

      <div className="bg-white border border-[#EAE7DF] rounded-2xl p-5 sm:p-7 mb-8">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#F1F4ED] flex items-center justify-center shrink-0">
            <ShieldCheck size={22} className="text-[#7C8B6E]" weight="fill" />
          </div>
          <div>
            <h3 className="font-editorial text-xl mb-1">La tua privacy non si tocca.</h3>
            <p className="text-[14px] text-[#5A584F] leading-relaxed">
              Nome, indirizzo, telefono ed email sono <strong>cifrati e mai mostrati</strong> agli agenti
              finché non sei tu a scegliere chi contattare. Niente spam, niente decine di telefonate.
            </p>
          </div>
        </div>
      </div>

      <button
        onClick={onStart}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-[#1C1C1A] text-white font-semibold text-[15px] hover:bg-[#000] transition-colors"
        data-testid="vendi-cta-start"
      >
        Inizia · &egrave; gratuita <ArrowRight size={18} weight="bold" />
      </button>
      <p className="mt-3 text-[11.5px] uppercase tracking-[0.18em] text-[#9A968B]">
        <Sparkle size={10} weight="fill" className="inline -mt-0.5 mr-1 text-amber-700" />
        Nessuna commissione · nessun obbligo · nessun login
      </p>

      <div className="mt-10 border-t border-[#EAE7DF] pt-7" data-testid="vendi-faq">
        <h3 className="font-editorial text-xl mb-4">Domande rapide</h3>
        <div className="space-y-4 text-[14px] text-[#3F3D38] leading-relaxed">
          <div>
            <p className="font-semibold text-[#1C1C1A] mb-1">Posso vendere casa senza un agente tramite Pronto?</p>
            <p className="text-[#5A584F]"><strong>No.</strong> Pronto serve a far incontrare proprietari e agenti immobiliari professionisti, non a sostituirli. La vendita avviene sempre tramite <strong>mandato regolare</strong> con un agente abilitato.</p>
          </div>
          <div>
            <p className="font-semibold text-[#1C1C1A] mb-1">Chi sono gli agenti che mi propongono?</p>
            <p className="text-[#5A584F]">Solo agenti immobiliari della tua zona, <strong>con partita IVA, iscrizione al ruolo</strong> (FIMAA / FIAIP / Confcommercio) e verifica Pronto. Niente privati, niente intermediari abusivi.</p>
          </div>
          <div>
            <p className="font-semibold text-[#1C1C1A] mb-1">Quanto costa a me?</p>
            <p className="text-[#5A584F]">Zero. Pronto non chiede nulla al proprietario: né iscrizione, né commissione. La provvigione la paghi solo all'agente con cui firmi il mandato, alle condizioni che vi siete accordati.</p>
          </div>
        </div>
      </div>
    </main>
  );
}

function Step({ n, icon: Icon, title, children }) {
  return (
    <div className="bg-white border border-[#EAE7DF] rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-widest font-bold text-[#7C8B6E]">0{n}</span>
        <Icon size={16} weight="duotone" className="text-[#7C8B6E]" />
      </div>
      <h4 className="font-semibold text-[15px] mb-1.5">{title}</h4>
      <p className="text-[12.5px] text-[#5A584F] leading-snug">{children}</p>
    </div>
  );
}

function Form({ form, setF, onBack, onSubmit, submitting, refAgent }) {
  return (
    <main className="max-w-2xl mx-auto px-5 lg:px-8 pt-8 pb-20">
      <button onClick={onBack} className="text-[12px] text-[#5A6A4D] mb-4 inline-flex items-center gap-1 hover:text-[#1C1C1A]" data-testid="vendi-form-back">
        <ArrowLeft size={14} /> Indietro
      </button>
      {refAgent && <RefAgentBanner agent={refAgent} variant="form" />}
      <h2 className="font-editorial text-3xl mb-2">La tua casa</h2>
      <p className="text-[14px] text-[#5A584F] mb-6">Bastano 2 minuti. Puoi modificare tutto dopo.</p>

      <form onSubmit={onSubmit} className="space-y-5" data-testid="vendi-form">
        <Section title="Dove si trova">
          <Row>
            <Field label="Città *" required>
              <input value={form.city} onChange={(e) => setF("city", e.target.value)} placeholder="Milano" className={input} required data-testid="vendi-city" />
            </Field>
            <Field label="Zona / Quartiere">
              <input value={form.area} onChange={(e) => setF("area", e.target.value)} placeholder="Brera, Centro, Isola..." className={input} data-testid="vendi-area" />
            </Field>
          </Row>
        </Section>

        <Section title="Caratteristiche">
          <Row>
            <Field label="Metri quadrati *">
              <input type="number" value={form.surface_sqm} onChange={(e) => setF("surface_sqm", e.target.value)} placeholder="100" className={input} required min="10" max="2000" data-testid="vendi-sqm" />
            </Field>
            <Field label="Locali *">
              <input type="number" value={form.rooms} onChange={(e) => setF("rooms", e.target.value)} placeholder="3" className={input} required min="1" max="20" data-testid="vendi-rooms" />
            </Field>
          </Row>
          <Row>
            <Field label="Bagni">
              <input type="number" value={form.bathrooms} onChange={(e) => setF("bathrooms", e.target.value)} placeholder="2" className={input} min="0" max="10" data-testid="vendi-baths" />
            </Field>
            <Field label="Stato">
              <select value={form.state} onChange={(e) => setF("state", e.target.value)} className={input} data-testid="vendi-state">
                <option value="nuovo">Nuovo</option>
                <option value="ottimo">Ottimo</option>
                <option value="buono">Buono</option>
                <option value="da_ristrutturare">Da ristrutturare</option>
              </select>
            </Field>
          </Row>
        </Section>

        <Section title="Foto della casa (opzionale, ma fortemente consigliato)" description="Aumenta del 4× la probabilità di ricevere proposte qualificate. Max 6 foto, vengono compresse automaticamente.">
          <PhotoUploader
            value={form.photos}
            onChange={(arr) => setF("photos", arr)}
            max={6}
            testid="vendi-photo-uploader"
          />
        </Section>

        <Section title="Il tuo obiettivo">
          <Row>
            <Field label="Quanto pensi di ottenere (€)">
              <input type="number" value={form.target_price} onChange={(e) => setF("target_price", e.target.value)} placeholder="850000" className={input} data-testid="vendi-target-price" />
            </Field>
            <Field label="Tempistiche">
              <select value={form.timing} onChange={(e) => setF("timing", e.target.value)} className={input} data-testid="vendi-timing">
                <option value="subito">Subito</option>
                <option value="1-3-mesi">Entro 3 mesi</option>
                <option value="3-6-mesi">3 — 6 mesi</option>
                <option value="6-12-mesi">6 — 12 mesi</option>
                <option value="esplorativo">Sto solo esplorando</option>
              </select>
            </Field>
          </Row>
          <Field label="Perché vendi (privato — visibile solo per dare contesto)">
            <input value={form.motivation} onChange={(e) => setF("motivation", e.target.value)} placeholder="Trasloco lavoro, eredità, casa più grande..." className={input} data-testid="vendi-motivation" />
          </Field>
          <Field label="Note libere (opzionale)">
            <textarea value={form.notes} onChange={(e) => setF("notes", e.target.value)} placeholder="Es. cantina inclusa, balcone con vista, ristrutturato 2 anni fa..." className={input + " min-h-[80px]"} data-testid="vendi-notes" />
          </Field>
        </Section>

        <Section title={<><EyeSlash size={16} weight="duotone" className="inline -mt-0.5 mr-1 text-[#7C8B6E]" />I tuoi contatti</>} description="Visibili SOLO agli agenti che sceglierai. Mai pubblici.">
          <Field label="Nome e cognome *">
            <input value={form.owner_name} onChange={(e) => setF("owner_name", e.target.value)} placeholder="Marco Ferrari" className={input} required data-testid="vendi-owner-name" />
          </Field>
          <Row>
            <Field label="Email *">
              <input type="email" value={form.owner_email} onChange={(e) => setF("owner_email", e.target.value)} placeholder="marco@email.it" className={input} required data-testid="vendi-owner-email" />
            </Field>
            <Field label="Telefono *">
              <input type="tel" value={form.owner_phone} onChange={(e) => setF("owner_phone", e.target.value)} placeholder="+39 333 1234567" className={input} required data-testid="vendi-owner-phone" />
            </Field>
          </Row>
          <label className="flex items-start gap-2 text-[12px] text-[#5A584F]">
            <input type="checkbox" checked={form.consent_marketing} onChange={(e) => setF("consent_marketing", e.target.checked)} className="mt-0.5" data-testid="vendi-consent" />
            <span>Acconsento a ricevere aggiornamenti via email da Pronto sulle proposte ricevute.</span>
          </label>
        </Section>

        <button
          type="submit"
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-[#1C1C1A] text-white font-semibold text-[15px] hover:bg-[#000] transition-colors disabled:opacity-60"
          data-testid="vendi-submit"
        >
          {submitting ? "Invio in corso..." : <>Pubblica la mia casa <ArrowRight size={18} weight="bold" /></>}
        </button>
      </form>
    </main>
  );
}

function Done({ result, refAgent }) {
  const link = result?.owner_url ? window.location.origin + result.owner_url : "";
  return (
    <main className="max-w-2xl mx-auto px-5 lg:px-8 pt-10 sm:pt-16 pb-20 text-center" data-testid="vendi-done">
      <div className="w-16 h-16 rounded-full bg-[#F1F4ED] flex items-center justify-center mx-auto mb-5">
        <CheckCircle size={32} weight="fill" className="text-[#7C8B6E]" />
      </div>
      <h2 className="font-editorial text-3xl sm:text-4xl mb-3 leading-tight">
        Fatto! La tua casa è nel marketplace.
      </h2>
      <p className="text-[15px] text-[#3F3D38] leading-relaxed mb-7 max-w-md mx-auto">
        {result?.message || "Riceverai un'email appena arriveranno le prime proposte (di solito 24-72h)."}
      </p>
      {refAgent && (
        <div className="bg-[#F1F4ED] border border-[#7C8B6E]/30 rounded-xl p-4 text-left mb-6" data-testid="vendi-done-ref">
          <p className="text-[13.5px] text-[#3F3D38] leading-relaxed">
            <strong className="text-[#5A6A4D]">{refAgent.name}</strong>
            {refAgent.agency_name ? ` (${refAgent.agency_name})` : ""} ha ricevuto una notifica:
            saprà subito che la sua segnalazione è arrivata e potrà mandarti la prima proposta di mandato.
          </p>
        </div>
      )}
      <div className="bg-white border border-[#EAE7DF] rounded-xl p-4 text-left mb-6">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#7C8B6E] mb-1.5">Il tuo link privato</div>
        <div className="font-mono text-[12px] break-all text-[#1C1C1A] mb-2.5">{link}</div>
        <p className="text-[12px] text-[#5A584F] leading-snug">Salva questo link: solo tu puoi vedere le proposte ricevute. Lo abbiamo anche inviato alla tua email.</p>
      </div>
      <a
        href={result?.owner_url || "#"}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#7C8B6E] text-white text-[14px] font-semibold hover:bg-[#5A6A4D]"
        data-testid="vendi-go-proposte"
      >
        Vai alle mie proposte <ArrowRight size={16} weight="bold" />
      </a>
    </main>
  );
}

// styles
const input = "w-full px-3 py-2.5 rounded-md border border-[#EAE7DF] bg-white text-[14px] text-[#1C1C1A] placeholder:text-[#9A968B] focus:outline-none focus:ring-2 focus:ring-[#7C8B6E]/40 focus:border-[#7C8B6E]";

function Section({ title, description, children }) {
  return (
    <div className="bg-white border border-[#EAE7DF] rounded-xl p-4 sm:p-5">
      <h3 className="font-editorial text-lg mb-1 flex items-center">{title}</h3>
      {description && <p className="text-[12px] text-[#5A584F] mb-3">{description}</p>}
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Row({ children }) {
  return <div className="grid sm:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, children, required }) {
  return (
    <div>
      <label className="block text-[11.5px] font-semibold text-[#3F3D38] mb-1">{label}{required && <span className="text-[#7C8B6E]">*</span>}</label>
      {children}
    </div>
  );
}


function RefAgentBanner({ agent, variant = "intro" }) {
  if (!agent) return null;
  const compact = variant === "form";
  return (
    <div
      className={`bg-white border-2 border-[#7C8B6E]/50 rounded-xl ${compact ? "p-3 mb-4" : "p-4 sm:p-5 mb-6"} flex items-center gap-3 sm:gap-4`}
      data-testid="vendi-ref-banner"
    >
      <div className="shrink-0">
        {agent.picture ? (
          <img src={agent.picture} alt={agent.name} className={`${compact ? "w-10 h-10" : "w-12 h-12 sm:w-14 sm:h-14"} rounded-full object-cover border border-[#EAE7DF]`} />
        ) : (
          <div className={`${compact ? "w-10 h-10" : "w-12 h-12 sm:w-14 sm:h-14"} rounded-full bg-[#F1F4ED] text-[#5A6A4D] flex items-center justify-center font-bold`}>
            {(agent.name || "A").charAt(0).toUpperCase()}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#7C8B6E] mb-0.5">
          Ti ha invitato
        </div>
        <div className={`${compact ? "text-[14px]" : "text-[15px] sm:text-[17px]"} font-semibold text-[#1C1C1A] leading-tight truncate`}>
          {agent.name}
        </div>
        {(agent.agency_name || agent.agency_address) && (
          <div className="text-[11.5px] text-[#5A584F] truncate">
            {agent.agency_name}{agent.agency_name && agent.agency_address ? " · " : ""}{agent.agency_address}
          </div>
        )}
        {!compact && (
          <p className="text-[11.5px] text-[#5A584F] mt-1.5 leading-snug">
            Quando completi il form, riceverà subito una notifica e potrà mandarti la prima proposta di mandato per la tua casa.
          </p>
        )}
      </div>
    </div>
  );
}
